import { useEffect, useRef } from 'react';
import type { HubSection } from '@/components/Hub/hubSectionTypes';
import { useHubActiveSection } from '@/components/Hub/useHubActiveSection';
import { focusRingClassNames } from '@/components/ui/utils';
import { useTheme } from '@/contexts/ThemeContext/useTheme';
import { trackingAttributes } from '@/lib/heap/trackingAttributes';
import { cn } from '@/lib/utils';

const ROW_EDGE_PADDING_PX = 8;

const NavIcon = ({ src, className }: { src: string; className?: string }) => (
    <img src={src} alt="" width={78} height={94} draggable={false} className={cn('-mt-1 -mb-1.5', className)} />
);

export interface HubNavProps {
    sections: readonly HubSection[];
    className?: string;
}

export const HubNav = ({ sections, className }: HubNavProps) => {
    const { theme } = useTheme();
    const navRef = useRef<HTMLElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const { activeId, scrollToSection } = useHubActiveSection(sections.map(section => section.id), navRef);

    // Row scroll only since `scrollIntoView` would also scroll the sheet and the page.
    useEffect(() => {
        const row = rowRef.current;
        const button = row?.querySelector<HTMLElement>('[aria-current="true"]');
        if (!row || !button) {
            return;
        }
        const left = button.offsetLeft - ROW_EDGE_PADDING_PX;
        const right = button.offsetLeft + button.offsetWidth + ROW_EDGE_PADDING_PX;
        if (left < row.scrollLeft) {
            row.scrollTo({ left });
        }
        else if (right > row.scrollLeft + row.clientWidth) {
            row.scrollTo({ left: right - row.clientWidth });
        }
    }, [activeId]);

    return (
        <nav ref={navRef} aria-label="Hub sections" className={cn('sticky top-0 z-10', className)}>
            <div
                ref={rowRef}
                tabIndex={-1}
                className={cn(
                    'scrollbar-hide relative flex overflow-x-auto overscroll-x-contain border-b px-7 py-1 outline-none',
                    'light:border-gz-neutral-200 dark:border-gz-neutral-300',
                )}
            >
                {sections.map((section) => {
                    const isActive = section.id === activeId;
                    const icons = section.navIcons[theme];
                    return (
                        <button
                            key={section.id}
                            type="button"
                            aria-label={section.title}
                            aria-current={isActive ? 'true' : undefined}
                            onClick={() => scrollToSection(section.id)}
                            {...trackingAttributes({
                                click: `Hub ${section.title} Nav Clicked`,
                                location: 'hub-sheet',
                                buttonName: `hub-nav-${section.id}`,
                            })}
                            className={cn('group flex shrink-0 grow justify-center rounded-md', focusRingClassNames, 'focus-visible:outline-offset-0')}
                        >
                            {isActive
                                ? <NavIcon src={icons.selected} />
                                : (
                                        <>
                                            <NavIcon src={icons.idle} className="group-hover:hidden" />
                                            {/* Both are mounted so the hover swap never waits on a fetch. */}
                                            <NavIcon src={icons.selected} className="hidden group-hover:block" />
                                        </>
                                    )}
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};
